import React, { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { Spinner, Table, TextInput } from 'flowbite-react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { HiOutlineArrowLeft, HiOutlineEye, HiOutlineSearch } from 'react-icons/hi';

import Button from '../../../components/Button';
import Notification from '../../../components/Notification';
import ToastNotification from '../../../components/ToastNotification';
import DefaultModal from '../../../components/modals/DefaultModal';
import DeleteModal from '../../../components/modals/DeleteModal';
import { ToastType } from '../../../typesData/ToastNotificationTypes';
import { formatMessage } from '../strings';
import { copyToClipboard, formatDate } from '../helpers';
import {
  addBatchOrders,
  BatchMutationResult,
  createBatch,
  DEFAULT_PAGE_SIZE,
  fetchBatchCandidates,
  fetchBatchDetail,
  fetchBatchManifestCsv,
  removeBatchOrder,
  removeBatchOrders,
  shipBatch,
} from './api';
import { batchDisplayId, downloadBlob, packageProgressColor } from './helpers';
import { batchesStrings } from './strings';
import { BatchDetail } from './types';

// id forces <Notification> to remount per banner — the shared component
// hides itself via a mount-once timeout and never resets, so reusing the
// same element renders nothing after the first 5-second window (matches
// TicketDetail.tsx's banner pattern).
type Banner = { id: number; type: ToastType; message: string };
let bannerSequence = 0;
const nextBanner = (type: ToastType, message: string): Banner => ({
  id: (bannerSequence += 1),
  type,
  message,
});

const DETAIL_QUERY_KEY = 'print-shop-batch';
const CANDIDATES_QUERY_KEY = 'print-shop-batch-candidates';
const LIST_QUERY_KEY = 'print-shop-batches';
const COUNTS_QUERY_KEY = 'print-shop-batch-counts';

const SEARCH_DEBOUNCE_MS = 300;

/** Short display form of an order id (last 8 chars), for table rows. */
const shortId = (id: string): string =>
  id.length > 8 ? `…${id.slice(-8).toUpperCase()}` : id.toUpperCase();

/**
 * The open-batch detail/builder (ADR-002): two client-side modes for one
 * page. `new` mode (no `batchId` in the route) shows only the candidates
 * table; the first "Add to batch" click calls `createBatch` and navigates
 * (replacing history) into `existing` mode with the returned `batch.id`.
 * `existing` mode (`/print-shop/batches/:batchId`) additionally shows the
 * "in this batch" table, the sticky package/order summary, manifest
 * download, and ship.
 */
export default function BatchBuilder() {
  const { batchId } = useParams<{ batchId?: string }>();
  const isNewMode = !batchId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [banner, setBanner] = useState<Banner | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(
    new Set(),
  );
  // Selection for the "in this batch" table — intentionally separate state
  // from `selectedOrderIds` (the candidates table's own selection) so the
  // two tables' checkboxes never collide.
  const [selectedBatchOrderIds, setSelectedBatchOrderIds] = useState<
    Set<string>
  >(new Set());
  const [candidateSearchInput, setCandidateSearchInput] = useState('');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidatePage, setCandidatePage] = useState(1);
  const [idCopied, setIdCopied] = useState(false);
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [trackingRef, setTrackingRef] = useState('');
  const [confirmEmptyShip, setConfirmEmptyShip] = useState(false);
  // Confirmation gating for order removal from the batch. Exactly one of
  // these is "active" at a time; both drive the single shared DeleteModal
  // below (mirrors this file's own shipModalOpen/confirmEmptyShip pattern).
  const [pendingRemoveOrderId, setPendingRemoveOrderId] = useState<
    string | null
  >(null);
  const [bulkRemoveConfirmOpen, setBulkRemoveConfirmOpen] = useState(false);

  // Debounce the candidates search box, same pattern as the ticket queue.
  useEffect(() => {
    const timer = setTimeout(() => {
      setCandidateSearch(candidateSearchInput.trim());
      setCandidatePage(1);
      setSelectedOrderIds(new Set());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [candidateSearchInput]);

  const {
    data: batch,
    isLoading: batchLoading,
    isError: batchLoadError,
  } = useQuery<BatchDetail>(
    [DETAIL_QUERY_KEY, batchId],
    () => fetchBatchDetail(batchId as string),
    { enabled: !isNewMode, refetchOnWindowFocus: false },
  );

  const {
    data: candidatesPage,
    isLoading: candidatesLoading,
    isError: candidatesLoadError,
  } = useQuery(
    [CANDIDATES_QUERY_KEY, candidateSearch, candidatePage],
    () =>
      fetchBatchCandidates({
        search: candidateSearch,
        page: candidatePage,
        limit: DEFAULT_PAGE_SIZE,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false },
  );

  /**
   * Every mutating action MUST invalidate all four query keys — the detail,
   * candidates, batches LIST, and counts/badge queries — not just the two
   * this page reads from. Otherwise the Batches list page and the Sidebar's
   * open-batch badge (task_07) go stale for the rest of the session.
   */
  const invalidateBatchQueries = (resolvedBatchId: string) => {
    queryClient.invalidateQueries([DETAIL_QUERY_KEY, resolvedBatchId]);
    queryClient.invalidateQueries(CANDIDATES_QUERY_KEY);
    queryClient.invalidateQueries(LIST_QUERY_KEY);
    queryClient.invalidateQueries(COUNTS_QUERY_KEY);
  };

  const notifyAddResult = (result: BatchMutationResult, orderIds: string[]) => {
    const message =
      result.skipped.length > 0
        ? formatMessage(batchesStrings.builder.candidates.addPartialSuccess, {
            added: String(orderIds.length - result.skipped.length),
            skipped: String(result.skipped.length),
          })
        : formatMessage(batchesStrings.builder.candidates.addSuccess, {
            count: String(orderIds.length),
          });
    setBanner(nextBanner('SUCCESS', message));
    ToastNotification({ type: 'success', message });
  };

  const createBatchMutation = useMutation(
    (orderIds: string[]) => createBatch(orderIds),
    {
      onSuccess: (result, orderIds) => {
        invalidateBatchQueries(result.batch.id);
        setSelectedOrderIds(new Set());
        notifyAddResult(result, orderIds);
        // Replace history so back-navigation doesn't return to the empty
        // new-mode state (ADR-002).
        navigate(`/print-shop/batches/${result.batch.id}`, { replace: true });
      },
      onError: () => {
        const message = batchesStrings.builder.candidates.addError;
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  const addBatchOrdersMutation = useMutation(
    (orderIds: string[]) => addBatchOrders(batchId as string, orderIds),
    {
      onSuccess: (result, orderIds) => {
        invalidateBatchQueries(batchId as string);
        setSelectedOrderIds(new Set());
        notifyAddResult(result, orderIds);
      },
      onError: () => {
        const message = batchesStrings.builder.candidates.addError;
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  const removeBatchOrderMutation = useMutation(
    (orderId: string) => removeBatchOrder(batchId as string, orderId),
    {
      onSuccess: (_updated, orderId) => {
        invalidateBatchQueries(batchId as string);
        const message = formatMessage(
          batchesStrings.builder.orders.removeSuccess,
          { orderId },
        );
        setBanner(nextBanner('SUCCESS', message));
        ToastNotification({ type: 'success', message });
      },
      onError: (_error, orderId) => {
        const message = formatMessage(
          batchesStrings.builder.orders.removeError,
          { orderId },
        );
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  const removeBatchOrdersMutation = useMutation(
    (orderIds: string[]) => removeBatchOrders(batchId as string, orderIds),
    {
      onSuccess: (result, orderIds) => {
        invalidateBatchQueries(batchId as string);
        setSelectedBatchOrderIds(new Set());
        const message =
          result.skipped.length > 0
            ? formatMessage(
                batchesStrings.builder.orders.removeSelectedPartialSuccess,
                {
                  removed: String(orderIds.length - result.skipped.length),
                  skipped: String(result.skipped.length),
                },
              )
            : formatMessage(
                batchesStrings.builder.orders.removeSelectedSuccess,
                { count: String(orderIds.length) },
              );
        setBanner(nextBanner('SUCCESS', message));
        ToastNotification({ type: 'success', message });
      },
      onError: () => {
        const message = batchesStrings.builder.orders.removeSelectedError;
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  const shipBatchMutation = useMutation(
    (ref: string | undefined) => shipBatch(batchId as string, ref),
    {
      onSuccess: () => {
        invalidateBatchQueries(batchId as string);
        const message = formatMessage(batchesStrings.ship.success, {
          batchId: batchDisplayId(batchId as string),
        });
        setBanner(nextBanner('SUCCESS', message));
        ToastNotification({ type: 'success', message });
      },
      onError: () => {
        const message = formatMessage(batchesStrings.ship.error, {
          batchId: batchDisplayId(batchId as string),
        });
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
      onSettled: () => {
        setShipModalOpen(false);
        setTrackingRef('');
        setConfirmEmptyShip(false);
      },
    },
  );

  const manifestMutation = useMutation(
    () => fetchBatchManifestCsv(batchId as string),
    {
      onSuccess: (blob) => {
        downloadBlob(blob, `batch-manifest-${batchId}.csv`);
      },
      onError: () => {
        const message = batchesStrings.manifest.downloadError;
        setBanner(nextBanner('FAILURE', message));
        ToastNotification({ type: 'failure', message });
      },
    },
  );

  const toggleSelected = (orderId: string) => {
    setSelectedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const toggleBatchOrderSelected = (orderId: string) => {
    setSelectedBatchOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  // Confirm-triggered — no longer called directly from the header button.
  const confirmBulkRemove = () => {
    const orderIds = Array.from(selectedBatchOrderIds);
    if (orderIds.length === 0 || removeBatchOrdersMutation.isLoading) {
      return;
    }
    removeBatchOrdersMutation.mutate(orderIds);
  };

  // Confirm-triggered — no longer called directly from the row's Remove button.
  const confirmSingleRemove = () => {
    if (!pendingRemoveOrderId || removeBatchOrderMutation.isLoading) {
      return;
    }
    removeBatchOrderMutation.mutate(pendingRemoveOrderId);
  };

  // The single shared DeleteModal's handleDelete — branches on which kind
  // of removal is pending.
  const handleConfirmRemove = () => {
    if (pendingRemoveOrderId) {
      confirmSingleRemove();
    } else {
      confirmBulkRemove();
    }
  };

  const closeRemoveConfirm = (show: boolean) => {
    if (!show) {
      setPendingRemoveOrderId(null);
      setBulkRemoveConfirmOpen(false);
    }
  };

  const candidates = candidatesPage?.data ?? [];
  const candidatesLastPage = Math.max(1, candidatesPage?.meta?.lastPage ?? 1);
  const selectedPackageCount = candidates
    .filter((candidate) => selectedOrderIds.has(candidate.orderId))
    .reduce((sum, candidate) => sum + candidate.packageCount, 0);

  const addIsLoading =
    createBatchMutation.isLoading || addBatchOrdersMutation.isLoading;

  const handleAddToBatch = () => {
    const orderIds = Array.from(selectedOrderIds);
    if (orderIds.length === 0 || addIsLoading) {
      return;
    }
    if (isNewMode) {
      createBatchMutation.mutate(orderIds);
    } else {
      addBatchOrdersMutation.mutate(orderIds);
    }
  };

  const onCopyFullId = async () => {
    if (!batchId) {
      return;
    }
    const ok = await copyToClipboard(batchId);
    if (ok) {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1500);
    }
  };

  const openShipModal = () => {
    setTrackingRef('');
    setConfirmEmptyShip(false);
    setShipModalOpen(true);
  };

  const handleShipConfirm = () => {
    if (shipBatchMutation.isLoading) {
      return;
    }
    if (trackingRef.trim() === '' && !confirmEmptyShip) {
      setConfirmEmptyShip(true);
      return;
    }
    shipBatchMutation.mutate(
      trackingRef.trim() === '' ? undefined : trackingRef.trim(),
    );
  };

  // Once a batch is no longer `open` (i.e. it has shipped), the backend
  // itself rejects further mutation (add/remove/ship all 409); the builder
  // must not show affordances for actions the API will refuse. `new` mode
  // has no batch yet, so it is always editable.
  const isEditable = isNewMode || batch?.status === 'open';

  const packageCount = batch?.packageCount ?? 0;
  const targetPackages = batch?.targetPackages ?? 0;
  const progressColor = batch
    ? packageProgressColor(packageCount, targetPackages)
    : 'amber';
  const progressBarColor =
    progressColor === 'green' ? 'bg-green-500' : 'bg-warn-500';
  const progressWidth =
    targetPackages > 0
      ? Math.min(100, Math.round((packageCount / targetPackages) * 100))
      : 0;

  return (
    <div className="pt-8 min-h-screen w-full pb-24">
      <ToastContainer />
      <div className="mb-4">
        <Link
          to="/print-shop/batches"
          className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-800"
        >
          <HiOutlineArrowLeft className="h-4 w-4" />
          {batchesStrings.builder.backToBatches}
        </Link>
      </div>

      {banner && (
        <Notification
          key={banner.id}
          type={banner.type}
          message={banner.message}
        />
      )}

      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1
          data-testid="builder-page-title"
          className="text-xl font-semibold font-mono"
        >
          {isNewMode
            ? batchesStrings.builder.newPageTitle
            : formatMessage(batchesStrings.builder.pageTitle, {
                batchId: batchDisplayId(batchId as string),
              })}
        </h1>
        {!isNewMode && (
          <button
            type="button"
            data-testid="copy-full-batch-id"
            onClick={onCopyFullId}
            className="text-xs border border-neutral-300 rounded-md px-2 py-1 text-neutral-500"
          >
            {idCopied
              ? batchesStrings.builder.copiedId
              : batchesStrings.builder.copyFullId}
          </button>
        )}
      </div>

      {!isNewMode && batch?.createdAt && (
        <div className="text-sm text-neutral-500 mb-6">
          {formatDate(batch.createdAt)}
          {batch.status === 'shipped' && (
            <>
              {' · '}
              <span data-testid="batch-shipped-meta">
                {batch.trackingRef
                  ? formatMessage(
                      batchesStrings.builder.shippedMetaWithTracking,
                      {
                        date: formatDate(batch.shippedAt),
                        trackingRef: batch.trackingRef,
                      },
                    )
                  : formatMessage(batchesStrings.builder.shippedMeta, {
                      date: formatDate(batch.shippedAt),
                    })}
              </span>
            </>
          )}
        </div>
      )}

      {!isNewMode && batchLoading && (
        <div className="py-10 flex justify-center">
          <Spinner size="lg" aria-label={batchesStrings.builder.loading} />
        </div>
      )}

      {!isNewMode && !batchLoading && batchLoadError && (
        <Notification
          type="FAILURE"
          message={batchesStrings.builder.loadError}
        />
      )}

      {(isNewMode || (!batchLoading && batch)) && (
        <>
          {/* Candidates: both modes, but only while the batch is still open —
              a shipped batch can no longer accept orders (the backend 409s),
              so the "Add to batch" panel is not just disabled, it's gone. */}
          {isEditable && (
            <div className="bg-white rounded-lg border border-neutral-200 mb-4">
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
                <div className="text-sm font-semibold">
                  {batchesStrings.builder.candidates.sectionTitle}
                </div>
                <TextInput
                  data-testid="candidates-search"
                  icon={HiOutlineSearch}
                  placeholder={
                    batchesStrings.builder.candidates.searchPlaceholder
                  }
                  value={candidateSearchInput}
                  onChange={(event) =>
                    setCandidateSearchInput(event.target.value)
                  }
                  className="max-w-xs"
                />
              </div>

              <div className="flex items-center justify-between px-4 py-2 bg-primary-50/40 border-b border-neutral-200 text-sm">
                <span
                  data-testid="candidates-selected-summary"
                  className="font-semibold text-neutral-700"
                >
                  {formatMessage(
                    batchesStrings.builder.candidates.selectedSummary,
                    {
                      count: String(selectedOrderIds.size),
                      packages: String(selectedPackageCount),
                    },
                  )}
                </span>
                <Button
                  variant="PRIMARY"
                  disabled={selectedOrderIds.size === 0 || addIsLoading}
                  isProcessing={addIsLoading}
                  onClick={handleAddToBatch}
                >
                  {addIsLoading
                    ? batchesStrings.builder.candidates.addingButton
                    : batchesStrings.builder.candidates.addButton}
                </Button>
              </div>

              {candidatesLoadError ? (
                <div className="px-4 py-4">
                  <Notification
                    type="FAILURE"
                    message={batchesStrings.builder.candidates.loadError}
                  />
                </div>
              ) : candidatesLoading ? (
                <div className="px-4 py-10 flex justify-center">
                  <Spinner aria-label={batchesStrings.builder.loading} />
                </div>
              ) : candidates.length === 0 ? (
                <p className="px-4 py-6 text-sm text-neutral-500">
                  {batchesStrings.builder.candidates.empty}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="candidates-table">
                    <Table.Head className="text-[11px] uppercase tracking-wide text-neutral-400">
                      <Table.HeadCell className="w-8" />
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.order}
                      </Table.HeadCell>
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.member}
                      </Table.HeadCell>
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.asset}
                      </Table.HeadCell>
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.packages}
                      </Table.HeadCell>
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.printedAt}
                      </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {candidates.map((candidate) => (
                        <Table.Row
                          key={candidate.orderId}
                          data-testid={`candidate-row-${candidate.orderId}`}
                          onClick={() => toggleSelected(candidate.orderId)}
                          className="cursor-pointer hover:bg-neutral-50"
                        >
                          <Table.Cell
                            className="py-2"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              data-testid={`candidate-checkbox-${candidate.orderId}`}
                              checked={selectedOrderIds.has(candidate.orderId)}
                              onChange={() => toggleSelected(candidate.orderId)}
                              className="h-4 w-4 accent-primary-500"
                            />
                          </Table.Cell>
                          <Table.Cell className="font-mono text-xs">
                            {shortId(candidate.orderId)}
                          </Table.Cell>
                          <Table.Cell>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-neutral-800">
                                {candidate.memberName}
                              </span>
                              <span className="text-xs text-neutral-400">
                                {candidate.memberEmail}
                              </span>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="text-sm">
                            {candidate.assetTitle}
                          </Table.Cell>
                          <Table.Cell className="text-sm">
                            {candidate.packageCount}
                          </Table.Cell>
                          <Table.Cell className="text-sm text-neutral-500">
                            {formatDate(candidate.printedAt)}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              )}

              {candidatesLastPage > 1 && (
                <div className="flex items-center justify-end gap-3 border-t border-neutral-100 px-4 py-3 text-sm text-neutral-500">
                  <Button
                    variant="OUTLINED"
                    disabled={candidatePage <= 1}
                    onClick={() =>
                      setCandidatePage((current) => Math.max(1, current - 1))
                    }
                  >
                    {batchesStrings.list.prev}
                  </Button>
                  <span data-testid="candidates-page-indicator">
                    {formatMessage(batchesStrings.list.pageIndicator, {
                      currentPage: String(candidatePage),
                      lastPage: String(candidatesLastPage),
                    })}
                  </span>
                  <Button
                    variant="OUTLINED"
                    disabled={candidatePage >= candidatesLastPage}
                    onClick={() => setCandidatePage((current) => current + 1)}
                  >
                    {batchesStrings.list.next}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* In this batch: existing mode only. */}
          {!isNewMode && batch && (
            <div className="bg-white rounded-lg border border-neutral-200 mb-4">
              <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
                <div className="text-sm font-semibold">
                  {batchesStrings.builder.orders.sectionTitle}
                </div>
                {isEditable && selectedBatchOrderIds.size > 0 && (
                  <Button
                    variant="DANGER"
                    size="xs"
                    disabled={removeBatchOrdersMutation.isLoading}
                    isProcessing={removeBatchOrdersMutation.isLoading}
                    onClick={() => setBulkRemoveConfirmOpen(true)}
                  >
                    {formatMessage(
                      batchesStrings.builder.orders.removeSelectedButton,
                      { count: String(selectedBatchOrderIds.size) },
                    )}
                  </Button>
                )}
              </div>
              {batch.orders.length === 0 ? (
                <p className="px-4 py-6 text-sm text-neutral-500">
                  {batchesStrings.builder.orders.empty}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="batch-orders-table">
                    <Table.Head className="text-[11px] uppercase tracking-wide text-neutral-400">
                      {isEditable && <Table.HeadCell className="w-8" />}
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.order}
                      </Table.HeadCell>
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.member}
                      </Table.HeadCell>
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.asset}
                      </Table.HeadCell>
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.packages}
                      </Table.HeadCell>
                      <Table.HeadCell>
                        {batchesStrings.builder.columns.actions}
                      </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {batch.orders.map((order) => (
                        <Table.Row
                          key={order.orderId}
                          data-testid={`batch-order-row-${order.orderId}`}
                          onClick={
                            isEditable
                              ? () => toggleBatchOrderSelected(order.orderId)
                              : undefined
                          }
                          className={
                            isEditable
                              ? 'cursor-pointer hover:bg-neutral-50'
                              : ''
                          }
                        >
                          {isEditable && (
                            <Table.Cell
                              className="py-2"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                data-testid={`batch-order-checkbox-${order.orderId}`}
                                checked={selectedBatchOrderIds.has(
                                  order.orderId,
                                )}
                                onChange={() =>
                                  toggleBatchOrderSelected(order.orderId)
                                }
                                className="h-4 w-4 accent-primary-500"
                              />
                            </Table.Cell>
                          )}
                          <Table.Cell className="font-mono text-xs">
                            {shortId(order.orderId)}
                          </Table.Cell>
                          <Table.Cell>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-neutral-800">
                                {order.memberName}
                              </span>
                              <span className="text-xs text-neutral-400">
                                {order.memberEmail}
                              </span>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="text-sm">
                            {order.assetTitle}
                          </Table.Cell>
                          <Table.Cell className="text-sm">
                            {order.packageCount}
                          </Table.Cell>
                          <Table.Cell
                            className="text-right"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="LIGHT"
                                size="xs"
                                data-testid={`view-order-${order.orderId}`}
                                onClick={() =>
                                  navigate(
                                    `/print-shop/tickets/${order.orderId}`,
                                  )
                                }
                              >
                                <HiOutlineEye className="mr-1 h-4 w-4" />
                                {batchesStrings.builder.orders.viewButton}
                              </Button>
                              {isEditable && (
                                <Button
                                  variant="DANGER"
                                  size="xs"
                                  data-testid={`remove-order-${order.orderId}`}
                                  disabled={removeBatchOrderMutation.isLoading}
                                  onClick={() =>
                                    setPendingRemoveOrderId(order.orderId)
                                  }
                                >
                                  {batchesStrings.builder.orders.removeButton}
                                </Button>
                              )}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Sticky package/order summary — only meaningful once a batch exists.
          The right-hand button group carries an extra `mr-24` beyond the
          bar's own `px-8`: the Jira issue-collector script (index.html,
          third-party — not ours to edit or z-index against) injects its own
          floating trigger fixed to the bottom-right of every page, and
          without this clearance it visually collides with these buttons.
          Reserving space is more robust than a z-index fight we can't win
          against a script we don't control. */}
      {!isNewMode && batch && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 px-8 py-3 flex flex-wrap items-center justify-between gap-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-3">
            <span
              data-testid="package-summary"
              className="text-sm font-semibold"
            >
              {formatMessage(batchesStrings.builder.summary.packageProgress, {
                count: String(packageCount),
                target: String(targetPackages),
              })}
            </span>
            <div className="w-40 h-2 bg-neutral-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${progressBarColor}`}
                style={{ width: `${progressWidth}%` }}
              />
            </div>
            <span className="text-xs text-neutral-400">
              {formatMessage(batchesStrings.builder.summary.orderCount, {
                count: String(batch.orderCount),
              })}
            </span>
          </div>
          <div className="flex items-center gap-3 mr-24">
            <Button
              variant="OUTLINED"
              isProcessing={manifestMutation.isLoading}
              disabled={manifestMutation.isLoading}
              onClick={() => manifestMutation.mutate()}
            >
              {batchesStrings.manifest.downloadButton}
            </Button>
            {isEditable ? (
              <Button variant="PRIMARY" onClick={openShipModal}>
                {batchesStrings.ship.button}
              </Button>
            ) : (
              <span
                data-testid="batch-shipped-indicator"
                className="inline-flex items-center gap-1.5 rounded-lg border border-success-200 bg-success-50 px-4 py-2 text-sm font-semibold text-success-700"
              >
                {batchesStrings.builder.summary.shippedBadge}
              </span>
            )}
          </div>
        </div>
      )}

      <DefaultModal
        title={
          confirmEmptyShip
            ? batchesStrings.ship.noTrackingTitle
            : formatMessage(batchesStrings.ship.dialogTitle, {
                batchId: batchId ? batchDisplayId(batchId) : '',
              })
        }
        showModal={shipModalOpen}
        setShowModal={(show) => {
          if (!show) {
            setShipModalOpen(false);
            setConfirmEmptyShip(false);
          }
        }}
        size="md"
      >
        <div className="flex flex-col gap-4">
          {confirmEmptyShip ? (
            <p className="text-sm text-neutral-800">
              {formatMessage(batchesStrings.ship.noTrackingMessage, {
                batchId: batchId ? batchDisplayId(batchId) : '',
              })}
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="ship-tracking-ref"
                className="text-sm font-semibold text-neutral-800"
              >
                {batchesStrings.ship.trackingLabel}
              </label>
              <TextInput
                id="ship-tracking-ref"
                data-testid="ship-tracking-ref"
                value={trackingRef}
                placeholder={batchesStrings.ship.trackingPlaceholder}
                onChange={(event) => setTrackingRef(event.target.value)}
              />
              <span className="text-xs text-neutral-400">
                {batchesStrings.ship.trackingHint}
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="OUTLINED"
              onClick={() => {
                setShipModalOpen(false);
                setConfirmEmptyShip(false);
              }}
            >
              {batchesStrings.ship.cancelButton}
            </Button>
            <Button
              variant="PRIMARY"
              isProcessing={shipBatchMutation.isLoading}
              disabled={shipBatchMutation.isLoading}
              onClick={handleShipConfirm}
            >
              {confirmEmptyShip
                ? batchesStrings.ship.noTrackingConfirm
                : batchesStrings.ship.confirmButton}
            </Button>
          </div>
        </div>
      </DefaultModal>

      <DeleteModal
        title={
          pendingRemoveOrderId
            ? batchesStrings.builder.orders.removeConfirmTitle
            : batchesStrings.builder.orders.removeSelectedConfirmTitle
        }
        showModal={pendingRemoveOrderId !== null || bulkRemoveConfirmOpen}
        setShowModal={closeRemoveConfirm}
        handleDelete={handleConfirmRemove}
        deleteButton={batchesStrings.builder.orders.removeConfirmButton}
      >
        <p className="text-sm text-neutral-800">
          {pendingRemoveOrderId
            ? formatMessage(batchesStrings.builder.orders.removeConfirmMessage, {
                orderId: pendingRemoveOrderId,
              })
            : formatMessage(
                batchesStrings.builder.orders.removeSelectedConfirmMessage,
                { count: String(selectedBatchOrderIds.size) },
              )}
        </p>
      </DeleteModal>
    </div>
  );
}
